export interface PendingEvent {
  item_id: string
  response: unknown
  idempotency_key: string
  client_time: string
}

const PREFIX = 'oap-exp1/drafts/'

export function draftStorageKey(examId: string, participantId: string): string {
  return `${PREFIX}${encodeURIComponent(examId)}/${encodeURIComponent(participantId)}`
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `evt-${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
}

export function createEvent(itemId: string, response: unknown): PendingEvent {
  return {
    item_id: itemId,
    response,
    idempotency_key: newIdempotencyKey(),
    client_time: new Date().toISOString(),
  }
}

export function parseDrafts(raw: string | null): PendingEvent[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isPendingEvent)
  } catch {
    return []
  }
}

function isPendingEvent(value: unknown): value is PendingEvent {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.item_id === 'string' &&
    candidate.item_id.length > 0 &&
    typeof candidate.idempotency_key === 'string' &&
    candidate.idempotency_key.length >= 8 &&
    typeof candidate.client_time === 'string'
  )
}

export function loadDrafts(key: string): PendingEvent[] {
  if (typeof localStorage === 'undefined') return []
  try {
    return parseDrafts(localStorage.getItem(key))
  } catch {
    return []
  }
}

export function storeDrafts(key: string, events: PendingEvent[]): void {
  if (typeof localStorage === 'undefined') return
  try {
    if (events.length === 0) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, JSON.stringify(events))
    }
  } catch {
    // Storage can be full or disabled. Answers remain in memory and the
    // autosave loop is still allowed to reach the server.
  }
}

/** Keep one latest event per item; the newest answer always wins. */
export function upsertDraft(events: PendingEvent[], event: PendingEvent): PendingEvent[] {
  return [...events.filter((candidate) => candidate.item_id !== event.item_id), event]
}

export function removeAcked(events: PendingEvent[], sentKeys: string[]): PendingEvent[] {
  const acked = new Set(sentKeys)
  return events.filter((event) => !acked.has(event.idempotency_key))
}
